import { IFile } from '@/models';
import { useFileStore } from '@/store';

export const fileSizeConverter = (bytes = 0, decimals = 0, onlyMB = false) => {
  const K_UNIT = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];

  if (!bytes) return '0B';

  if (onlyMB) return (bytes / (K_UNIT * K_UNIT)).toFixed(decimals) + 'MB';

  const i = Math.floor(Math.log(bytes) / Math.log(K_UNIT));
  const resp =
    parseFloat((bytes / Math.pow(K_UNIT, i)).toFixed(decimals)) + sizes[i];

  return resp;
};

export const createFileFromUrl = async (url: string) => {
  const response = await fetch(url);
  const data = await response.blob();
  const metadata = { type: data.type };
  const filename = url.replace(/\?.+/, '').split('/').pop() || '';
  return new File([data], filename, metadata);
};

export function readFile(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      return resolve(event?.target?.result as ArrayBuffer);
    };
    reader.onerror = (event) => {
      reader.abort();
      return reject(event);
    };
    reader.readAsArrayBuffer(file);
  });
}

export const getFileType = (
  mimeType?: string,
): 'video' | 'document' | 'image' | 'audio' | 'draft' => {
  if (!mimeType) return 'document';

  if (mimeType === 'DRAFT' || mimeType === 'DIR_RECORD') return 'draft';

  const docTypeFiles = ['application', 'text'];
  const fileType = mimeType.split('/')[0].toLowerCase() as
    | 'video'
    | 'document'
    | 'image'
    | 'audio';
  if (docTypeFiles.includes(fileType)) return 'document';
  return fileType;
};

export const generateFileFromBase64 = (
  base64String: string,
  fileName = 'image.png',
  fileType = 'image/png',
) => {
  const base64Data = base64String.replace(/^data:image\/png;base64,/, '');
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: fileType });
  return new File([blob], fileName, { type: fileType });
};

export const getFileExtension = (fileName?: string) => {
  if (!fileName) return '';

  const parts = fileName.split('.');
  return parts.length > 1 ? parts.pop() : '';
};

export const generateRandomFileName = (originFileName?: string) => {
  const fileExtension = getFileExtension(originFileName);
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(3, 10);
  let fileName = `${timestamp}-${randomString}`;

  if (fileExtension) {
    fileName += `.${fileExtension}`;
  }
  return fileName;
};

const errorMessages: Record<string, string> = {
  'file-too-large': 'File is too large (maximum 15GB)',
  'file-invalid-type': 'This file format is not supported.',
};

export const getErrorMessage = (
  errorCode?: string,
  defaultMessage?: string,
) => {
  return errorCode
    ? errorMessages[errorCode] || defaultMessage
    : defaultMessage;
};

export const getFileUrlWithValidToken = (
  file: IFile,
  updatedSasToken?: string,
): string => {
  // Get file URL with valid SAS token.
  const fileUrl = file.url?.split('?')[0] ?? '';
  const isTmpDirectory = fileUrl.includes('tmp');
  if (isTmpDirectory) {
    const fileSasToken = file.url?.split('?')[1];
    if (fileSasToken) {
      return `${fileUrl}?${fileSasToken}`;
    } else {
      // get tmp token from file state
      const tempFileSasToken =
        useFileStore?.getState()?.fileTokens?.tmp?.sasToken ?? '';
      return `${fileUrl}?${tempFileSasToken}`;
    }
  } else {
    // this makes sure expired sasToken is not attached to file url
    return `${fileUrl}?${updatedSasToken}`;
  }
};

export function validateFile(
  file: IFile,
  type = 'image',
  size = 30,
): string | boolean {
  // if file type is not 'image/*' and file size not less than 30kb
  // return error on file form field
  if (!file.mimeType.includes(type)) {
    return `file should be of type ${type}`;
  }

  if (file.size > size * 1024) {
    return `file size should be less than ${size}KB`;
  }

  return true;
}
